/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multimodal Chat Attachments & Ad-hoc RAG Document Parser (Task 10.3.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

export type ChatAttachmentType = 'image' | 'document' | 'audio';

export interface ChatAttachmentItem {
  id: string;
  type: ChatAttachmentType;
  name: string;
  size: number;
  formattedSize: string;
  mimeType: string;
  previewUrl?: string;
  extractedText?: string;
  uploadStatus: 'uploading' | 'ready' | 'error';
  errorMessage?: string;
}

export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
];

export const SUPPORTED_DOC_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv', '.json'];

export const MAX_IMAGE_SIZE_BYTES = 15 * 1024 * 1024; // 15MB
export const MAX_DOC_SIZE_BYTES = 25 * 1024 * 1024; // 25MB

/**
 * Format bytes to human-readable string (KB, MB, GB).
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

const generateAttachmentId = () =>
  `att_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

export class ChatAttachmentService {
  /**
   * Determine attachment type from MIME type and file extension.
   */
  static getAttachmentType(file: File): ChatAttachmentType {
    if (SUPPORTED_IMAGE_TYPES.includes(file.type) || file.type.startsWith('image/')) {
      return 'image';
    }
    if (file.type.startsWith('audio/')) {
      return 'audio';
    }
    return 'document';
  }

  /**
   * Validates file size and MIME type.
   */
  static validateFile(file: File): { valid: boolean; error?: string } {
    const type = this.getAttachmentType(file);

    if (type === 'image') {
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        return {
          valid: false,
          error: `Kích thước ảnh vượt quá giới hạn cho phép (${formatFileSize(MAX_IMAGE_SIZE_BYTES)})`,
        };
      }
      return { valid: true };
    }

    if (file.size > MAX_DOC_SIZE_BYTES) {
      return {
        valid: false,
        error: `Kích thước tệp tài liệu vượt quá giới hạn (${formatFileSize(MAX_DOC_SIZE_BYTES)})`,
      };
    }

    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const isSupportedDoc =
      SUPPORTED_DOC_EXTENSIONS.includes(ext) ||
      file.type.includes('text') ||
      file.type.includes('pdf') ||
      file.type.includes('word') ||
      file.type.includes('json');

    if (!isSupportedDoc && type !== 'audio') {
      return {
        valid: false,
        error: `Định dạng tệp ${ext} chưa được hỗ trợ. Hãy dùng PDF, DOCX, TXT, MD, CSV hoặc JSON.`,
      };
    }

    return { valid: true };
  }

  /**
   * Reads an image file and produces an attachment item with thumbnail preview.
   */
  static async processImageFile(file: File): Promise<ChatAttachmentItem> {
    const validation = this.validateFile(file);
    const id = generateAttachmentId();

    if (!validation.valid) {
      return {
        id,
        type: 'image',
        name: file.name,
        size: file.size,
        formattedSize: formatFileSize(file.size),
        mimeType: file.type || 'image/png',
        uploadStatus: 'error',
        errorMessage: validation.error,
      };
    }

    // Modern browser with FileReader
    if (typeof FileReader !== 'undefined') {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            id,
            type: 'image',
            name: file.name,
            size: file.size,
            formattedSize: formatFileSize(file.size),
            mimeType: file.type || 'image/png',
            previewUrl: reader.result as string,
            uploadStatus: 'ready',
          });
        };
        reader.onerror = () => {
          resolve({
            id,
            type: 'image',
            name: file.name,
            size: file.size,
            formattedSize: formatFileSize(file.size),
            mimeType: file.type || 'image/png',
            uploadStatus: 'error',
            errorMessage: 'Lỗi khi đọc file ảnh từ thiết bị',
          });
        };
        reader.readAsDataURL(file);
      });
    }

    // Node.js / Vitest environment fallback
    try {
      const buffer = await file.arrayBuffer();
      const nodeBuffer = (globalThis as unknown as { Buffer?: { from: (b: ArrayBuffer) => { toString: (enc: string) => string } } }).Buffer;
      const base64 = nodeBuffer
        ? nodeBuffer.from(buffer).toString('base64')
        : btoa(String.fromCharCode(...new Uint8Array(buffer)));

      return {
        id,
        type: 'image',
        name: file.name,
        size: file.size,
        formattedSize: formatFileSize(file.size),
        mimeType: file.type || 'image/png',
        previewUrl: `data:${file.type || 'image/png'};base64,${base64}`,
        uploadStatus: 'ready',
      };
    } catch {
      return {
        id,
        type: 'image',
        name: file.name,
        size: file.size,
        formattedSize: formatFileSize(file.size),
        mimeType: file.type || 'image/png',
        uploadStatus: 'ready',
      };
    }
  }

  /**
   * Reads a document file and extracts text for ad-hoc RAG injection.
   */
  static async processDocumentFile(file: File): Promise<ChatAttachmentItem> {
    const validation = this.validateFile(file);
    const id = generateAttachmentId();

    if (!validation.valid) {
      return {
        id,
        type: 'document',
        name: file.name,
        size: file.size,
        formattedSize: formatFileSize(file.size),
        mimeType: file.type || 'application/octet-stream',
        uploadStatus: 'error',
        errorMessage: validation.error,
      };
    }

    const isTextReadable =
      file.type.includes('text') ||
      file.name.endsWith('.txt') ||
      file.name.endsWith('.md') ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.json');

    if (isTextReadable) {
      try {
        let text = '';
        if (typeof file.text === 'function') {
          text = await file.text();
        } else if (typeof FileReader !== 'undefined') {
          text = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve((reader.result as string) || '');
            reader.onerror = reject;
            reader.readAsText(file);
          });
        }

        return {
          id,
          type: 'document',
          name: file.name,
          size: file.size,
          formattedSize: formatFileSize(file.size),
          mimeType: file.type || 'text/plain',
          extractedText: text,
          uploadStatus: 'ready',
        };
      } catch {
        return {
          id,
          type: 'document',
          name: file.name,
          size: file.size,
          formattedSize: formatFileSize(file.size),
          mimeType: file.type || 'text/plain',
          uploadStatus: 'error',
          errorMessage: 'Lỗi khi đọc nội dung văn bản',
        };
      }
    }

    // For binary formats like PDF/DOCX, mark as ready for backend ad-hoc processing
    return {
      id,
      type: 'document',
      name: file.name,
      size: file.size,
      formattedSize: formatFileSize(file.size),
      mimeType: file.type || 'application/pdf',
      extractedText: `[Tài liệu đính kèm: ${file.name} - Kích thước: ${formatFileSize(file.size)}]`,
      uploadStatus: 'ready',
    };
  }

  /**
   * Handles clipboard paste events to extract pasted images.
   */
  static async handlePasteEvent(
    event: React.ClipboardEvent | ClipboardEvent
  ): Promise<ChatAttachmentItem[]> {
    const items = event.clipboardData?.items;
    if (!items || items.length === 0) return [];

    const attachments: ChatAttachmentItem[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          // Provide a descriptive timestamp name for pasted screenshot
          const renamedFile = new File(
            [file],
            `screenshot_${new Date().toISOString().replace(/[:.]/g, '-')}.png`,
            { type: file.type }
          );
          const att = await this.processImageFile(renamedFile);
          attachments.push(att);
        }
      }
    }

    return attachments;
  }

  /**
   * Processes a list of dropped or selected files.
   */
  static async processMultipleFiles(files: FileList | File[]): Promise<ChatAttachmentItem[]> {
    const fileArray = Array.from(files);
    const results: ChatAttachmentItem[] = [];

    for (const file of fileArray) {
      const type = this.getAttachmentType(file);
      if (type === 'image') {
        const img = await this.processImageFile(file);
        results.push(img);
      } else {
        const doc = await this.processDocumentFile(file);
        results.push(doc);
      }
    }

    return results;
  }
}
